import { useMemo } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useAnnotationPreferences } from '@/hooks/use-annotation-preferences';
import { cn } from '@/lib/utils';

interface AnnotationVisibilityToggleProps {
  className?: string;
  align?: 'start' | 'center' | 'end';
  size?: 'sm' | 'md';
  mode?: 'default' | 'icon';
  disabled?: boolean;
}

export function AnnotationVisibilityToggle({ className, align = 'end', size = 'md', mode = 'default', disabled = false }: AnnotationVisibilityToggleProps) {
  const {
    preferences: { showAnnotations, includeSeen },
    setShowAnnotations,
    setIncludeSeen,
  } = useAnnotationPreferences();

  const toggleLabel = useMemo(() => (showAnnotations ? 'Hide annotations' : 'Show annotations'), [showAnnotations]);

  const isIconMode = mode === 'icon';
  const buttonSize = isIconMode ? 'icon' : size === 'sm' ? 'sm' : 'default';
  const buttonClassName = cn(
    isIconMode ? 'inline-flex items-center justify-center h-9 w-9' : 'flex items-center gap-2',
    'text-[#D7DADC]/90 hover:text-white transition-colors disabled:text-[#5A5B5F] disabled:hover:text-[#5A5B5F]',
    className,
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={buttonSize}
          className={buttonClassName}
          role="switch"
          aria-checked={showAnnotations}
          aria-label={toggleLabel}
          disabled={disabled}
        >
          {showAnnotations && !disabled ? <Pin className="h-4 w-4" /> : <PinOff className="h-4 w-4" />}
          {!isIconMode && (
            <span className="text-sm font-medium hidden sm:inline">{toggleLabel}</span>
          )}
        </Button>
      </PopoverTrigger>
      {!disabled && (
        <PopoverContent align={align} className="w-60 space-y-4 bg-[#1F1F23] border-[#343536] text-[#E5E7EB]">
          <div className="space-y-1">
            <h4 className="text-sm font-semibold">Post annotations</h4>
            <p className="text-xs text-[#9CA3AF]">
              Artist notes appear as pins over the post. They fade after you open them once.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <Label htmlFor="toggle-annotations" className="text-sm">Show annotations</Label>
              <span className="text-xs text-[#9CA3AF]">Pins appear on posts when enabled.</span>
            </div>
            <Switch
              id="toggle-annotations"
              checked={showAnnotations}
              onCheckedChange={setShowAnnotations}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <Label htmlFor="toggle-include-seen" className="text-sm">Include seen notes</Label>
              <span className="text-xs text-[#9CA3AF]">Show pins you already opened once.</span>
            </div>
            <Switch
              id="toggle-include-seen"
              checked={includeSeen}
              onCheckedChange={setIncludeSeen}
              disabled={!showAnnotations}
            />
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}

export default AnnotationVisibilityToggle;
